import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
	useGetHomeDashboardLayoutQuery,
	usePostResetHomeDashboardLayoutMutation,
	usePutHomeDashboardLayoutMutation,
} from '../../../slices/dashboardApiSlice';
import {
	buildDefaultHomeDashboardLayout,
	normalizeHomeDashboardLayout,
	reorderHomeDashboardSections,
	toggleHomeDashboardSectionHidden,
} from '../../../utils/dashboard/homeDashboardLayout';
import { scrollDashboardMainScrollToTop } from '../../../utils/appMainScroll';

const ENTER_EDITING_TOAST = '已进入布局编辑，可拖拽区块标题调整顺序';

const homeLayoutShellStorageKey = (userId) => (userId ? `erp-home-layout-shell:${userId}` : null);

const readHomeLayoutShell = (userId, role) => {
	const key = homeLayoutShellStorageKey(userId);
	if (!key || typeof window === 'undefined') return null;
	try {
		const parsed = JSON.parse(window.sessionStorage.getItem(key) || 'null');
		if (!parsed || typeof parsed !== 'object') return null;
		return normalizeHomeDashboardLayout(parsed, role);
	} catch {
		return null;
	}
};

const writeHomeLayoutShell = (userId, layout) => {
	const key = homeLayoutShellStorageKey(userId);
	if (!key || typeof window === 'undefined' || !layout) return;
	try {
		window.sessionStorage.setItem(key, JSON.stringify(layout));
	} catch {
		// ignore quota / private mode
	}
};

const useHomeDashboardLayout = (userInfo) => {
	const userId = userInfo?._id;
	const role = userInfo?.role ?? 'sale';
	const skip = !userId;
	const { data, isSuccess, isError } = useGetHomeDashboardLayoutQuery(undefined, { skip });
	const [putLayout, { isLoading: isSaving }] = usePutHomeDashboardLayoutMutation();
	const [resetLayout, { isLoading: isResetting }] = usePostResetHomeDashboardLayoutMutation();

	const [layout, setLayout] = useState(
		() => readHomeLayoutShell(userId, userInfo ?? role) ?? buildDefaultHomeDashboardLayout(userInfo ?? role),
	);
	const [isEditing, setIsEditing] = useState(false);
	const [actionMessage, setActionMessage] = useState('');
	const layoutRef = useRef(layout);
	const scrollOnEnterEditingRef = useRef(false);
	layoutRef.current = layout;

	useEffect(() => {
		if (skip) {
			setLayout(buildDefaultHomeDashboardLayout(userInfo ?? role));
			return;
		}
		if (isSuccess && data?.layout) {
			const next = normalizeHomeDashboardLayout(data.layout, userInfo ?? role);
			setLayout(next);
			writeHomeLayoutShell(userId, next);
		} else if (isError) {
			const fallback = readHomeLayoutShell(userId, userInfo ?? role) ?? buildDefaultHomeDashboardLayout(userInfo ?? role);
			setLayout(fallback);
		}
	}, [data?.layout, isError, isSuccess, role, skip, userId, userInfo]);

	useEffect(() => {
		if (!isEditing || !scrollOnEnterEditingRef.current) {
			return;
		}

		scrollOnEnterEditingRef.current = false;
		scrollDashboardMainScrollToTop({ smooth: true });
	}, [isEditing]);

	const showMessage = useCallback((message) => {
		setActionMessage(message);
		window.setTimeout(() => setActionMessage(''), 3200);
	}, []);

	const persistLayout = useCallback(
		(nextLayout) => {
			const normalized = normalizeHomeDashboardLayout(nextLayout, userInfo ?? role);
			setLayout(normalized);
			writeHomeLayoutShell(userId, normalized);
			if (!userId) return;
			putLayout(normalized);
		},
		[putLayout, role, userId],
	);

	const reorderSection = useCallback(
		(sourceId, targetId) => {
			const next = reorderHomeDashboardSections(layoutRef.current, sourceId, targetId, userInfo ?? role);
			persistLayout(next);
		},
		[persistLayout, role],
	);

	const toggleSectionHidden = useCallback(
		(sectionId) => {
			const next = toggleHomeDashboardSectionHidden(layoutRef.current, sectionId, userInfo ?? role);
			persistLayout(next);
		},
		[persistLayout, role],
	);

	const restoreDefaultLayout = useCallback(async () => {
		if (!userId) {
			const next = buildDefaultHomeDashboardLayout(userInfo ?? role);
			setLayout(next);
			return;
		}
		try {
			const result = await resetLayout().unwrap();
			const next = normalizeHomeDashboardLayout(result?.layout, userInfo ?? role);
			setLayout(next);
			writeHomeLayoutShell(userId, next);
			showMessage('已恢复默认布局');
		} catch {
			showMessage('恢复默认失败，请稍后重试');
		}
	}, [resetLayout, role, showMessage, userId]);

	const startEditing = useCallback(() => {
		scrollOnEnterEditingRef.current = true;
		setIsEditing(true);
		toast.info(ENTER_EDITING_TOAST, { autoClose: 3500 });
		showMessage(ENTER_EDITING_TOAST);
	}, [showMessage]);

	const finishEditing = useCallback(() => {
		setIsEditing(false);
		showMessage('首页布局已保存');
	}, [showMessage]);

	return {
		layout,
		isEditing,
		isSaving,
		isResetting,
		actionMessage,
		startEditing,
		finishEditing,
		reorderSection,
		toggleSectionHidden,
		restoreDefaultLayout,
	};
};

export default useHomeDashboardLayout;
